// const PARSED_DATA = {
//     ctrData: [123, 213, 2323223, 23223, 2323],
//     keywordRank: 23,
//     keywordResults: 23000,
//     serpData: [{
//         citationFlow: 45,
//         domainAuthority: 13,
//         externalBacklinks: 2455,
//         facebookShares: 2,
//         id: '213dasdsa1e23dasdas',
//         rank: 54,
//         refDomains: 1833,
//         refIPs: 1423,
//         refSubnets: 1211,
//         trustFlow: 71,
//         uniqueIPCount: 11488,
//         url: 'http://www.hotels.com/'
//     }]
// };

import { isNil } from 'ramda';
import Defaults from 'mangools-commons/dist/constants/Defaults';

import { parseIntWithNVFallback } from 'sources/parsers/commons';
import { FEATURE_TYPES } from 'constants/SerpFeatureTypes';

const isMajesticPresent = item => !isNil(item.m?.majestic);
const isFacebookPresent = item => !isNil(item.m?.fb);
const isMozPresent = item => !isNil(item.m?.moz);
const isRankPresent = item => !isNil(item.m?.rank);

const decodeUrl = rawUrl => {
    // NOTE:
    // decodeURIComponent coerces its argument to a string, so a missing URL would
    // become the literal 'undefined' and get rendered and linked as a real one.
    if (typeof rawUrl !== 'string') {
        return '';
    }

    try {
        const decodedUrl = decodeURIComponent(rawUrl);
        return decodedUrl;
    } catch (e) {
        // NOTE:
        // i.e. malformed URI error
        // we are fallbacking to the original URL, it should come from SERP so we believe in it :)
        return rawUrl;
    }
};

const parseSerpItemData = (item) => {
    const isFeaturedSnippet = item.type === FEATURE_TYPES.FEATURED_SNIPPET;

    return {
        citationFlow: isMajesticPresent(item)
            ? parseIntWithNVFallback(item.m.majestic.v.CitationFlow)
            : Defaults.NO_VALUE,
        domainAuthority: isMozPresent(item) ? parseIntWithNVFallback(item.m.moz.v.pda) : Defaults.NO_VALUE,
        externalBacklinks: isMajesticPresent(item)
            ? parseIntWithNVFallback(item.m.majestic.v.ExtBackLinks)
            : Defaults.NO_VALUE,
        facebookShares: isFacebookPresent(item) ? parseIntWithNVFallback(item.m.fb.v.l, 10) : Defaults.NO_VALUE,
        featuredSnippet: isFeaturedSnippet,
        type: item.type,
        ctr: item.ctr,
        id: item._id, // eslint-disable-line no-underscore-dangle
        contentType: item.contentType,
        kwOpps: item.kwOpps,
        pageAuthority: isMozPresent(item) ? parseIntWithNVFallback(item.m.moz.v.upa) : Defaults.NO_VALUE,
        rank: isRankPresent(item) ? parseIntWithNVFallback(item.m.rank.v.r, 10) : Defaults.NO_VALUE,
        refDomains: isMajesticPresent(item) ? parseIntWithNVFallback(item.m.majestic.v.RefDomains) : Defaults.NO_VALUE,
        refIPs: isMajesticPresent(item) ? parseIntWithNVFallback(item.m.majestic.v.RefIPs) : Defaults.NO_VALUE,
        refSubnets: isMajesticPresent(item) ? parseIntWithNVFallback(item.m.majestic.v.RefSubNets) : Defaults.NO_VALUE,
        trustFlow: isMajesticPresent(item) ? parseIntWithNVFallback(item.m.majestic.v.TrustFlow) : Defaults.NO_VALUE,
        url: decodeUrl(item.url),
    };
};

export const parseSerpData = rawData => {
    let organicCount = 0;
    const knoGraphItemIndex = rawData.findIndex(
        item => item.type === FEATURE_TYPES.KNO_GRAPH
    );

    const output = rawData.reduce((acc, item) => {
        if (item.type === FEATURE_TYPES.KNO_GRAPH) {
            return acc;
        }

        if (!isNil(FEATURE_TYPES[item.type])) {
            acc.push(parseSerpItemData(item));
        }

        if (item.type === FEATURE_TYPES.ORGANIC) {
            organicCount++;
        }

        // Business logic: Move KNO Graph after 3rd organic result
        if (organicCount === 3 && knoGraphItemIndex !== -1) {
            acc.push(parseSerpItemData(rawData[knoGraphItemIndex]));
        }
        return acc;
    }, []);
    return output;
};

export const parse = rawData => {
    if (!rawData) {
        return null;
    }

    const { rkID, contentTypes, searchIntent, kwOpps, serpFeatures, serp } = rawData;

    if (!serp) {
        return null;
    }

    const {
        _id,
        kw,
        rank,
        rank_updated_at,
        created_at,
        items,
    } = serp;

    return {
        _id,
        contentTypes,
        searchIntent,
        kwOpps,
        serpFeatures,
        kwId: rkID,
        kw,
        rank: parseIntWithNVFallback(rank),
        rankUpdatedAt: parseIntWithNVFallback(rank_updated_at),
        serpData: parseSerpData(items),
        updatedAt: parseIntWithNVFallback(created_at),
    };
};
